<script setup lang="ts">
import Icon from '@ui/components/icon/Icon.vue'
import { ChipVariant, Sizes } from '@ui/styles'
import AngleDown from '~icons/cue-regular/angle-down'
import Times from '~icons/cue-regular/times'

withDefaults(
  defineProps<{
    disabled?: boolean
    label?: string
    variant?: ChipVariant
    size?: typeof Sizes.Luke | typeof Sizes.HanSolo | typeof Sizes.R2D2
    open?: boolean
    onClose?: () => void
    onOpen?: () => void
  }>(),
  {
    disabled: false,
    label: '',
    variant: ChipVariant.Primary,
    size: Sizes.Luke,
    open: false,
  },
)
</script>

<template lang="pug">
button.chip.flex-center-center.row-8.border-none.cursor-pointer(
  :disabled="disabled"
  :class="[size, variant]"
)
  span {{ $t(label) }}
  Icon.s-20.flex-shrink-0(v-if="onClose" :name="Times" @click.stop="onClose")
  Icon.s-20.flex-shrink-0(v-if="onOpen" :class="{ open }" :name="AngleDown" @click.stop="onOpen")
</template>

<style scoped>
.chip {
  transition: 0.2s ease;
  transition-property: color, background-color;

  &.r2d2 {
    --uno: p-8-16 br-12 typ-14-20-500;
  }

  &.hanSolo {
    --uno: p-10-16 br-14 typ-14-20-500;
  }

  &.luke {
    --uno: p-12-16 br-16 typ-16-24-500;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--cue-border-primary-soft);
  }

  &.primary {
    color: var(--cue-content-neutral-secondary);
    background-color: var(--cue-bg-neutral-secondary);

    &:hover {
      background-color: var(--cue-bg-neutral-secondary-hover);
    }

    &:active {
      background-color: var(--cue-bg-neutral-secondary-pressed);
    }
  }

  &.secondary {
    color: var(--cue-content-neutral-secondary);
    background-color: var(--cue-bg-surface-primary);
    border: 1px solid var(--cue-border-neutral-secondary);

    &:hover {
      background-color: var(--cue-bg-neutral-secondary);
    }

    &:active {
      background-color: var(--cue-bg-neutral-secondary-hover);
    }

    &.r2d2 {
      --uno: p-7-15;
    }

    &.hanSolo {
      --uno: p-9-15;
    }

    &.luke {
      --uno: p-11-15;
    }
  }

  &.selected {
    color: var(--cue-content-primary-on-soft);
    background-color: var(--cue-bg-primary-soft-dark);
    border: 2px solid var(--cue-border-primary-default);

    &:hover {
      background-color: var(--cue-bg-primary-soft-hover);
    }

    &:active {
      background-color: var(--cue-bg-primary-soft-pressed);
    }

    &.r2d2 {
      --uno: p-6-14;
    }

    &.hanSolo {
      --uno: p-8-14;
    }

    &.luke {
      --uno: p-10-14;
    }
  }

  &:disabled {
    color: var(--cue-content-neutral-disabled);
    background: var(--cue-bg-neutral-disabled);
    cursor: not-allowed;
  }
}

.open {
  transform: rotate(180deg);
}
</style>
